import React, { useEffect, useState } from 'react';
import { Shield } from 'lucide-react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminStatus as fetchAdminStatus } from '../../services/adminAccessService';

type Props = {
  children: React.ReactNode;
};

const RequireAdmin: React.FC<Props> = ({ children }) => {
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!currentUser) {
        if (mounted) {
          setIsAdmin(false);
          setChecking(false);
        }
        return;
      }
      if (mounted) setChecking(true);
      try {
        const status = await fetchAdminStatus();
        if (mounted) setIsAdmin(status);
      } catch {
        if (mounted) setIsAdmin(false);
      } finally {
        if (mounted) setChecking(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [currentUser]);

  const onLogin = async () => {
    navigate('/app/login', { state: { from: location.pathname } });
  };

  const onLogout = async () => {
    setError(null);
    try {
      await logout();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Logout failed. Please try again.');
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-off-white flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center max-w-md">
          <div className="w-20 h-20 bg-coral/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield size={36} className="text-coral" />
          </div>
          <h1 className="font-nunito font-bold text-2xl text-charcoal mb-4">Admin Access Required</h1>
          <p className="text-gray-600 mb-8">
            Please sign in with your admin account (Google or Email). Phone OTP accounts without an email address cannot be admin.
          </p>
          <button
            onClick={onLogin}
            className="w-full bg-coral hover:bg-coral/90 text-white font-bold py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Sign in
          </button>
          {error ? (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          ) : null}
        </div>
      </div>
    );
  }

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-off-white flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral mx-auto mb-4"></div>
          <p className="text-gray-600">Checking access…</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-off-white flex items-center justify-center">
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield size={36} className="text-red-500" />
          </div>
          <h1 className="font-nunito font-bold text-2xl text-charcoal mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">You don't have permission to access this admin panel.</p>
          <div className="text-sm text-gray-500 mb-8">
            Signed in as {currentUser.email || currentUser.phoneNumber || 'unknown account'}. Admin access is email-based.
          </div>
          <button
            onClick={onLogout}
            className="w-full bg-gray-500 hover:bg-gray-600 text-white font-bold py-4 rounded-xl text-lg transition-all duration-300"
          >
            Sign Out
          </button>
          {error ? (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          ) : null}
        </div>
      </div>
    );
  }

  if (location.pathname === '/admin') {
    return <Navigate to="/admin/overview" replace />;
  }

  return <>{children}</>;
};

export default RequireAdmin;
